import { useCallback } from 'react';
import { parseTxt } from '../parsers/txtParser';
import { fetchUrl } from '../services/api';
import type { ParsedText, UrlFetchResult } from '../types';

/**
 * Client-side file parsing hook with lazy-loaded parsers.
 * - TXT: FileReader (loaded immediately, tiny)
 * - PDF: pdfjs-dist (lazy loaded ~147KB chunk)
 * - DOCX: mammoth (lazy loaded ~40KB chunk)
 * - EPUB: jszip (lazy loaded ~30KB chunk)
 * - URL: server-side via /api/fetch-url
 */
export function useFileParser() {
  const wrappedParseTxt = useCallback((file: File): Promise<ParsedText> => parseTxt(file), []);

  const wrappedParsePdf = useCallback(async (file: File): Promise<ParsedText> => {
    const { parsePdf } = await import(/* webpackChunkName: "pdf-parser" */ '../parsers/pdfParser');
    return parsePdf(file);
  }, []);

  const wrappedParseDocx = useCallback(async (file: File): Promise<ParsedText> => {
    const { parseDocx } = await import(
      /* webpackChunkName: "docx-parser" */ '../parsers/docxParser'
    );
    return parseDocx(file);
  }, []);

  const wrappedParseEpub = useCallback(async (file: File): Promise<ParsedText> => {
    const { parseEpub } = await import(
      /* webpackChunkName: "epub-parser" */ '../parsers/epubParser'
    );
    return parseEpub(file);
  }, []);

  const parseUrl = useCallback(async (url: string): Promise<UrlFetchResult> => {
    return fetchUrl(url);
  }, []);

  /**
   * Parse any supported file or URL.
   * @param {File|string} input — File object or URL string
   * @returns {{ text: string, title: string }}
   */
  const parse = useCallback(
    async (input: File | string): Promise<ParsedText | UrlFetchResult> => {
      if (typeof input === 'string') {
        return parseUrl(input);
      }

      const ext = input.name.split('.').pop()?.toLowerCase();

      switch (ext) {
        case 'txt':
        case 'md':
          return wrappedParseTxt(input);
        case 'pdf':
          return wrappedParsePdf(input);
        case 'doc':
        case 'docx':
          return wrappedParseDocx(input);
        case 'epub':
          return wrappedParseEpub(input);
        default:
          // Try as plain text for unknown extensions
          return wrappedParseTxt(input);
      }
    },
    [wrappedParseTxt, wrappedParsePdf, wrappedParseDocx, wrappedParseEpub, parseUrl]
  );

  return { parse };
}
